import React, { useCallback, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { Routes } from 'routes';
import { Colors } from '@nakedplatform/ui';
import { NakedIcon } from 'theme/icons';
import { Container } from 'theme/layout';

import { DEFAULT_OPTIONS } from './constants';

import {
  BackButton,
  LogoLink,
  NavSection,
  MenuContainer,
  MenuCloseButton,
  ItemList,
  ItemLink,
  ListItem,
  MenuButton,
  Overlay,
  SubItemLink,
  SubItemList,
  SubItemListItem,
  Avatar,
  AvatarContainer,
  CloseButton,
  RoseBoldText,
  RoseLightText,
} from './styles';
import ROSE_IMAGE from 'assets/images/rose.jpg';
import { useCoBranding } from '@nakedplatform/business';

const LOGO_SIZE = 20;

/**
 * Navigation menu for each product
 *
 * @param props - Render props
 */
export function AppNavigator(props: AppNavigatorProps) {
  const {
    centerAlign,
    variant = 'default',
    showSiteNav = true,
    showBackButton,
    goBack,
    goHome,
    isRose = false,
  } = props;
  const [isSideMenuOpen, toggleSideMenu] = useState(false);
  const router = useRouter();
  const isOverlayLight = variant === 'overlay-light';
  const actionIconColor = isOverlayLight ? Colors.white : Colors.black;
  const logoColor = isOverlayLight ? Colors.white : Colors.thunder;

  const handleOpenSideMenu = useCallback(() => {
    toggleSideMenu(!isSideMenuOpen);
  }, [isSideMenuOpen]);

  const handleOnBack = useCallback(() => {
    router.back();
  }, [router]);

  const { theme } = useCoBranding();
  const borderTopColor = theme?.palette?.activeColor;

  return (
    <NavSection
      borderBottom={isOverlayLight ? 'none' : `1px solid ${Colors.cararra}`}
      borderTopColor={borderTopColor}
      hideBorder={isOverlayLight}
      overlay={isOverlayLight}
      transparent={isOverlayLight}
    >
      {showSiteNav && (
        <MenuButton onClick={handleOpenSideMenu}>
          <NakedIcon name="menu" size={7} color={Colors.thunder} />
        </MenuButton>
      )}
      {showBackButton && (
        <BackButton onClick={goBack || handleOnBack} transparent={isOverlayLight}>
          <NakedIcon name="arrow-left" size={12} color={actionIconColor} />
        </BackButton>
      )}
      <NavigationLogo centerAlign={centerAlign} iconColor={logoColor} isRose={isRose} />
      {goHome && (
        <CloseButton onClick={goHome} transparent={isOverlayLight}>
          <NakedIcon name="remove" size={12} color={actionIconColor} />
        </CloseButton>
      )}
      <Overlay
        opacity={isSideMenuOpen ? 1 : 0}
        display={isSideMenuOpen ? 'block' : 'none'}
        onClick={handleOpenSideMenu}
      />
      {showSiteNav && (
        <MenuContainer>
          <ItemList translateX={isSideMenuOpen ? '0%' : '-100%'}>
            <ListItem>
              <ItemLink href={Routes.HOME} aria-label="home page link" onClick={handleOpenSideMenu}>
                <NakedIcon name="home" size={LOGO_SIZE} color={Colors.tundora} />
              </ItemLink>
            </ListItem>
            <ListItem>
              <ItemLink href={Routes.CAR_INSURANCE} onClick={handleOpenSideMenu}>
                My Car
              </ItemLink>
            </ListItem>
            <ListItem>
              <ItemLink className="sub-menu">My Home</ItemLink>
              <SubItemList>
                {DEFAULT_OPTIONS.dropdown.map(dropdown => (
                  <SubItemListItem key={dropdown.url}>
                    <SubItemLink href={dropdown.url} onClick={handleOpenSideMenu}>
                      {dropdown.title}
                    </SubItemLink>
                  </SubItemListItem>
                ))}
              </SubItemList>
            </ListItem>
            <ListItem>
              <ItemLink onClick={handleOpenSideMenu} href={Routes.PET_INSURANCE}>
                Pet
              </ItemLink>
            </ListItem>
            {DEFAULT_OPTIONS.hiddenItems.map(item => (
              <ListItem key={item.url}>
                <Link href={item.url} prefetch={false}>
                  <ItemLink onClick={handleOpenSideMenu} href={item.url}>
                    {item.title}
                  </ItemLink>
                </Link>
              </ListItem>
            ))}

            <ListItem>
              <MenuCloseButton onClick={handleOpenSideMenu} aria-label="close menu button">
                <NakedIcon name="close" size={12} color={Colors.white} />
              </MenuCloseButton>
            </ListItem>
          </ItemList>
        </MenuContainer>
      )}
    </NavSection>
  );
}
/**
 * Component to render naked logo or rose image
 *
 * @param props - Navigation logo props
 */
function NavigationLogo(props: NavigationLogoProps) {
  const { isRose, centerAlign, iconColor } = props;

  if (isRose) {
    return (
      <Container
        display="flex"
        flex={1}
        justifyContent={'center'}
        alignItems="center"
        flexDirection="row"
        px={15}
      >
        <AvatarContainer>
          <Avatar src={ROSE_IMAGE} />
        </AvatarContainer>
        <RoseBoldText>
          Rose <RoseLightText>from Naked</RoseLightText>
        </RoseBoldText>
      </Container>
    );
  }

  return (
    <LogoLink centerAlign={centerAlign} href={Routes.HOME} leftAlign={!centerAlign}>
      <NakedIcon name="naked-logo" size={LOGO_SIZE} color={iconColor} />
    </LogoLink>
  );
}
/** Type definitions */
export interface AppNavigatorProps {
  /** Center align the logo */
  centerAlign?: boolean;
  /** Visual treatment for the navigator */
  variant?: AppNavigatorVariant;
  /** Show side menu button */
  showSiteNav?: boolean;
  /** Flag to decide whether to show back button */
  showBackButton?: boolean;
  /** Action to go home */
  goHome?(): void;
  /** Action to go back */
  goBack?(): void;
  /** Show rose image instead of Naked logo */
  isRose?: boolean;
}

interface NavigationLogoProps {
  /** Show rose image instead of Naked logo */
  isRose: boolean;
  /** Center align the logo */
  centerAlign: boolean;
  /** Color used for the Naked logo */
  iconColor: string;
}

type AppNavigatorVariant = 'default' | 'overlay-light';
